<script setup lang="ts">
import { onMounted, ref, watch } from "vue";
import BbelField from "@/views/builder/components/bbel/BbelField.vue";
import type { ScopeDescriptor } from "@/types/bbel-types";

// Name → BBEL-value rows over a Record<string, string> content map (handler headers/query,
// onResponse directives, outbound shaping). Row-editor idiom: rows then the Add button below.
const props = defineProps<{
  modelValue: Record<string, string> | null | undefined;
  scope: ScopeDescriptor;
  label: string;
  addLabel: string;
  keyPlaceholder?: string;
  valuePlaceholder?: string;
  /** Valid key vocabulary, offered via a native datalist (suggests, never enforces). */
  keyOptions?: string[];
  /** Tooltip on the section label's info icon. */
  labelTooltip?: string;
}>();

const emit = defineEmits<{
  "update:modelValue": [value: Record<string, string> | null];
}>();

const entries = ref<Array<{ key: string; value: string }>>([]);

// Datalist ids are document-global — one per component instance.
const keyListId = `bbel-map-keys-${Math.random().toString(36).slice(2, 8)}`;

function initialize() {
  entries.value = Object.entries(props.modelValue ?? {}).map(([key, value]) => ({ key, value }));
}

// Half-typed rows (blank name) stay local; the map only carries named entries, and an empty map
// round-trips as null so an untouched section never dirties the draft.
function emitUpdate() {
  const map: Record<string, string> = {};
  for (const entry of entries.value) {
    const key = entry.key.trim();
    if (key) map[key] = entry.value;
  }
  emit("update:modelValue", Object.keys(map).length ? map : null);
}

function addRow() {
  entries.value.push({ key: "", value: "" });
}

function removeRow(index: number) {
  entries.value.splice(index, 1);
  emitUpdate();
}

onMounted(initialize);
// Re-sync when the bound map changes identity from outside (discard, method re-bind) — but not for
// our own emits, which round-trip an equal map.
watch(
  () => props.modelValue,
  (next) => {
    const current: Record<string, string> = {};
    for (const e of entries.value) if (e.key.trim()) current[e.key.trim()] = e.value;
    if (JSON.stringify(next ?? {}) !== JSON.stringify(current)) initialize();
  },
);
</script>

<template>
  <div class="mb-3">
    <label class="form-label">
      {{ label }}
      <i
        v-if="labelTooltip"
        class="bi bi-info-circle text-muted ms-1"
        data-bs-toggle="tooltip"
        :title="labelTooltip"
      ></i>
    </label>
    <datalist v-if="keyOptions?.length" :id="keyListId">
      <option v-for="opt in keyOptions" :key="opt" :value="opt"></option>
    </datalist>
    <!-- Pinned rows the host contributes — rendered above the editable entries, like
         HeadersEditor's connection ghost rows. -->
    <slot name="reserved"></slot>
    <div v-for="(entry, index) in entries" :key="index" class="d-flex align-items-stretch gap-2 mb-2">
      <input
        v-model="entry.key"
        type="text"
        class="form-control w-25 flex-shrink-0 font-monospace"
        :placeholder="keyPlaceholder ?? 'Name'"
        :list="keyOptions?.length ? keyListId : undefined"
        @input="emitUpdate"
      />
      <BbelField
        v-model="entry.value"
        class="flex-grow-1"
        :scope="scope"
        single-line
        :placeholder="valuePlaceholder ?? 'Value'"
        @update:model-value="emitUpdate"
      />
      <button type="button" class="btn btn-outline-danger btn-sm" @click="removeRow(index)">
        <i class="bi bi-trash"></i>
      </button>
    </div>
    <div>
      <button type="button" class="btn btn-outline-primary btn-sm" @click="addRow">
        <i class="bi bi-plus"></i> {{ addLabel }}
      </button>
    </div>
  </div>
</template>
